/* eslint-disable no-undefined */
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  PRODUCT_OPTION_INPUT_TYPE_CHECKBOX,
  PRODUCT_OPTION_INPUT_TYPE_QUANTITY,
  PRODUCT_OPTION_INPUT_TYPE_RADIO,
  PRODUCT_OPTION_INPUT_TYPES,
  resetCustomization,
  saveCustomization,
  selectCustomization,
  setCustomizationQuantity,
  toggleCustomizationChoice
} from 'store/slices/shopping-cart';

import formatPrice from '@youship/utils/format-price';

import Button from '@youship/components/objects/button';
import Checkbox from '@youship/components/objects/checkbox';
import CustomQuantity from '@youship/components/objects/custom-quantity';
import Modal from '@youship/components/objects/modal';
import Radio from '@youship/components/objects/radio';
import Quantity from '@youship/components/objects/quantity';


import './styles.scss';

const ShoppingCartItemCustomizationModal = () => {
  const dispatch = useDispatch();
  const customization = useSelector(selectCustomization);

  if (!customization?.product) return null;

  const { amountAvailable, choices, product } = customization;
  const { options } = product;
  let customizationIsValid = false;

  // product.limit value of 0 means that there is no limit
  customizationIsValid = customization.quantity > 0 && (!product.limit || customization.quantity <= product.limit) && options.every((option) => {
    const { id, mandatory } = option;
    const choiceExtrasIds = Object.keys(choices[id]);

    return !mandatory || choiceExtrasIds.some(extraId => choices[id][extraId]);
  });

  const handleChoiceSelect = (optionId, extraId, quantity) => {
    dispatch(toggleCustomizationChoice({ extraId, optionId, quantity }));
  };

  const handleClose = () => {
    dispatch(resetCustomization());
  };

  const handleQuantityChange = (quantity) => {
    dispatch(setCustomizationQuantity(quantity));
  };

  const handleSave = () => {
    dispatch(saveCustomization());
  };

  let title = customization.product.options.length ? `Customize ${customization.product.name}` : '';

  if (amountAvailable === 0) title = 'You have the available amount in your cart';

  return (
    <Modal
      classNames={`shopping-cart-item-customization-modal${!customization.product.options.length ? ' shopping-cart-item-customization-modal--no-body' : ''}`}
      footer={
        <div className="shopping-cart-item-customization-modal__footer">
          <div className="shopping-cart-item-customization-modal__footer-quantity-label">
            Quantity
          </div>
          <Quantity
            max={typeof amountAvailable === 'number' ? amountAvailable : null}
            min={1}
            value={customization.quantity}
            onChange={handleQuantityChange}
          />
          <Button
            context="primary"
            disabled={!customizationIsValid}
            largeHorizontalPadding
            text={typeof customization.index === 'number' ? 'Update' : 'Add to your order'}
            onClick={handleSave}
          />
        </div>
      }
      header={
        <div className="shopping-cart-item-customization-modal__header">
          <img
            alt="Product"
            className="shopping-cart-item-customization-modal__header-image"
            src={customization.product.imageUrl}
          />
          <div className="shopping-cart-item-customization-modal__header-content">
            <div className="shopping-cart-item-customization-modal__header-title">
              {customization.product.name}
            </div>
            <div className="shopping-cart-item-customization-modal__header-description">
              {customization.product.description}
            </div>
            <div className="shopping-cart-item-customization-modal__header-price">
              {formatPrice(customization.product.price?.value)}
            </div>
            {typeof amountAvailable === 'number' && (
              <div className="shopping-cart-item-customization-modal__header-limit">
                Limit:
                {' '}
                {amountAvailable}
              </div>
            )}
          </div>
        </div>
      }
      noScroll={!!customization.product.options.length}
      title={title}
      onClose={handleClose}
    >
      {customization.product.options.length ?
        (
          <div className="shopping-cart-item-customization-modal__body">
            {customization.product.options.map(option => PRODUCT_OPTION_INPUT_TYPES.includes(option.input) && (
              <div
                key={option.id}
                className="shopping-cart-item-customization-modal__option"
              >
                <div className="shopping-cart-item-customization-modal__option-title">
                  {option.name}
                  {!!option.subtitle && ` – ${option.subtitle}`}
                  {!!option.mandatory && ' (required)'}
                </div>
                {option.input === PRODUCT_OPTION_INPUT_TYPE_CHECKBOX && option.extras.map(extra => (
                  <div
                    key={extra.id}
                    className="shopping-cart-item-customization-modal__option-extra"
                  >
                    <Checkbox
                      checked={!!customization.choices[option.id] && !!customization.choices[option.id][extra.id]}
                      description={extra.description !== extra.name ? extra.description : null}
                      disabled={extra.soldOut || amountAvailable === 0}
                      label={extra.name}
                      note={typeof extra.price?.value === 'number' ? `+\xa0${formatPrice(extra.price?.value)}` : null}
                      noteContext="success"
                      onChange={() => handleChoiceSelect(option.id, extra.id)}
                    />
                  </div>
                ))}
                {option.input === PRODUCT_OPTION_INPUT_TYPE_QUANTITY && option.extras.map((extra) => {
                  // extra.limit or option.limit value of 0 means that there is no limit
                  const max = extra.limit && typeof extra.limit === 'number' && option.limit && typeof option.limit === 'number' ?
                    Math.min(extra.limit, option.limit) :
                    extra.limit || option.limit || null;

                  return (
                    <div
                      key={extra.id}
                      className="shopping-cart-item-customization-modal__option-extra"
                    >
                      <CustomQuantity
                        description={extra.description !== extra.name ? extra.description : null}
                        disabled={extra.soldOut || amountAvailable === 0}
                        label={extra.name}
                        max={max}
                        note={typeof extra.price?.value === 'number' ? `+\xa0${formatPrice(extra.price?.value)}` : null}
                        noteContext="success"
                        value={customization.choices[option.id] && typeof customization.choices[option.id][extra.id] === 'number' ?
                          customization.choices[option.id][extra.id] :
                          0}
                        onChange={value => handleChoiceSelect(option.id, extra.id, value)}
                      />
                    </div>
                  );
                })}
                {option.input === PRODUCT_OPTION_INPUT_TYPE_RADIO && option.extras.map(extra => (
                  <div
                    key={extra.id}
                    className="shopping-cart-item-customization-modal__option-extra"
                  >
                    <Radio
                      description={extra.description !== extra.name ? extra.description : null}
                      disabled={extra.soldOut || amountAvailable === 0}
                      label={extra.name}
                      note={typeof extra.price?.value === 'number' ? `+\xa0${formatPrice(extra.price?.value)}` : null}
                      noteContext="success"
                      selected={customization.choices[option.id] && customization.choices[option.id][extra.id] ? extra.id : null}
                      value={extra.id}
                      onChange={() => handleChoiceSelect(option.id, extra.id)}
                    />
                  </div>
                ))}
              </div>
            ))}
          </div>
        ) :
        null}
    </Modal>
  );
};

export default ShoppingCartItemCustomizationModal;
